import { FC, useEffect, useMemo, useState } from 'react';
import { Modal } from '@dynamika-shared/ui/Modal/Modal';
import { useForm } from 'react-hook-form';
import { Select } from '@dynamika-shared/ui/Select/Select';
import { Input } from '@dynamika-shared/ui/Input/Input';
import { yupResolver } from '@hookform/resolvers/yup';
import { DatePicker } from '@dynamika-shared/ui/DatePicker/DatePicker';
import * as yup from 'yup';
import { QuestionSignIcon } from '@dynamika-shared/ui/icons';
import { CohortType } from '@dynamika-shared/types/cohorts';
import { Helper } from 'src/pages/ClinicalTrial/ClinicalTrialVisits/CreateCohort';
import { isEmpty } from 'lodash';
import './subjectCreate.scss';
import { format } from 'date-fns';
import { TruncatedText } from '@dynamika-shared/ui/TruncatedText/TruncatedText';
import { UnsavedChangesModal } from '@components/modals/UnsavedChangesModal/UnsavedChangesModal';
import { FlattenOptionData } from 'rc-select/lib/interface';
import { BaseOptionType } from 'rc-select/lib/Select';
import { convertDateToUTC } from '@utils/DateTime';
import { patientCodePattern } from 'src/pages/SiteUser/SubjectList/Utils/SubjectListPatterns';

type SubjectCreateProps = {
  toggleVisibility: (isToggle: boolean) => void;
  onSubmit: (data: SiteSubjectTypeForm) => Promise<void>;
  cohorts: CohortType[] | undefined;
};

const genderValues = ['Male', 'Female'] as const;
const genderOptions = genderValues.map(gender => ({
  value: gender,
  label: gender,
}));

export interface SiteSubjectTypeForm {
  cohortId: string;
  patientCode: string;
  dob: string;
  gender: (typeof genderValues)[number] | undefined;
}

const getValidationSchema = (matchedCohort: CohortType | undefined) => {
  return yup.object().shape({
    cohortId: yup.string().required('Cohort is required'),
    patientCode: yup
      .string()
      .required('Subject ID is required')
      .max(50, 'Subject ID Pattern should not exceed 50 characters')
      .matches(
        patientCodePattern(matchedCohort) as RegExp,
        `The entered subject ID does not match the cohort pattern, please enter the Subject ID like ${matchedCohort?.patientCodePattern}`
      ),

    dob: yup.string().required('Birth date is required'),
    // gender is required if fetchPatientGender is true, otherwise it should be undefined
    gender: yup
      .mixed<(typeof genderValues)[number]>()
      .optional()
      .test('gender-validation', 'Gender is required', value => {
        const isRequired = !!matchedCohort?.fetchPatientGender;

        if (!isRequired) {
          return (
            value === undefined ||
            genderValues.includes(value as (typeof genderValues)[number])
          );
        }

        return (
          value !== undefined &&
          genderValues.includes(value as (typeof genderValues)[number])
        );
      }),
  });
};

export const SubjectCreate: FC<SubjectCreateProps> = ({
  toggleVisibility,
  onSubmit,
  cohorts,
}) => {
  const [askQuit, setAskQuit] = useState<boolean>(false);
  const [matchedCohort, setMatchedCohort] = useState<CohortType | undefined>(
    undefined
  );

  const {
    watch,
    setValue,
    getValues,
    trigger,
    reset,
    handleSubmit,
    formState,
    clearErrors,
  } = useForm<SiteSubjectTypeForm>({
    resolver: yupResolver(getValidationSchema(matchedCohort)),
    defaultValues: {
      cohortId: '',
      patientCode: '',
      dob: undefined,
      gender: undefined,
    },
  });

  const { errors, isValid, isSubmitting } = formState;

  const onChangeField = async (
    fieldName: keyof SiteSubjectTypeForm,
    value: string
  ) => {
    setValue(fieldName, value, { shouldDirty: true });
    trigger(fieldName);
  };

  const cohortOptions = useMemo(() => {
    return cohorts?.map(({ id, name }) => ({
      id,
      name,
      value: id,
      label: name,
    }));
  }, [cohorts]);

  const submit = async (data: SiteSubjectTypeForm) => {
    onSubmit(data).then(() => reset());
    setAskQuit(false);
  };

  useEffect(() => {
    const selectedCohort = cohorts?.find(c => c.id === getValues().cohortId);
    setMatchedCohort(selectedCohort);
  }, [cohorts, getValues().cohortId]);

  const onCloseModal = () => {
    if (!isEmpty(formState.dirtyFields)) {
      setAskQuit(true);
    } else {
      toggleVisibility(false);
    }
  };

  const optionRender = (option: FlattenOptionData<BaseOptionType>) => {
    return (
      <div className='flex items-center overflow-hidden'>
        <TruncatedText
          text={option.data.label}
          truncate
          tooltip
          className={'h-[44px] text-1624 font-medium leading-[44px]'}
        />
      </div>
    );
  };

  const dobDate = watch('dob') ? new Date(watch('dob') || new Date()) : null;
  const dobUTC = convertDateToUTC(dobDate);

  const selectedCohort = cohorts?.find(c => c.id === watch('cohortId'));
  const isFetchPatientGender = !!selectedCohort?.fetchPatientGender;

  return (
    <>
      <Modal
        data-testid='CreateSU'
        width={440}
        onCancel={() => onCloseModal()}
        onOk={handleSubmit(submit)}
        okProps={{
          htmlType: 'submit',
          disabled: !isValid || !watch('patientCode') || isSubmitting,
        }}
        okText='Add'
        maskClosable={false}
        open
        centered
        className='rounded-[32px]'
        classNames={{
          footer: '!mt-1 !pt-3',
        }}
        cancelClassName='w-[168px] h-[48px]'
        okClassName='w-[168px] h-[48px]'
        title={<div className='mb-4 text-left text-1824'>Add New Subject</div>}
      >
        <Select
          required
          showSearch
          filterOption={(input, option) =>
            ((option?.label ?? '') as string)
              .toLowerCase()
              .includes(input.toLowerCase())
          }
          errors={errors.cohortId?.message}
          options={cohortOptions}
          value={watch('cohortId')}
          name='cohort'
          onChange={selectedValue => {
            onChangeField('cohortId', selectedValue);
            setValue('patientCode', '');
            setValue('gender', undefined);
            clearErrors(['patientCode', 'gender']);
          }}
          optionRender={optionRender}
          label={'Cohort'}
          placeholder={'Choose Cohort'}
          data-testid='cohort'
          className='mb-3'
        />

        <Input
          disabled={!watch('cohortId')}
          defaultValue={watch('patientCode')}
          value={watch('patientCode')}
          name='patientCode'
          data-testid='patientCode'
          placeholder={selectedCohort?.patientCodePattern}
          onChange={e =>
            onChangeField('patientCode', e.target.value.toLocaleUpperCase())
          }
          errors={errors.patientCode?.message}
          classNameInputWrapper='mb-2'
          label={'Subject ID'}
          required
          extraHelper={<Helper icon={QuestionSignIcon} />}
        />

        <div className='field-required my-1 text-1424 font-semibold'>
          Date of Birth
        </div>

        <DatePicker
          picker='month'
          value={dobUTC}
          onChange={(date: Date | null) => {
            if (date) {
              onChangeField('dob', format(date, 'yyyy-MM'));
            } else {
              // Handle the case when the date is cleared
              onChangeField('dob', '');
            }
          }}
          disabledDate={(current: Date) => {
            const minDate = new Date();
            minDate.setFullYear(minDate.getFullYear() - 124);

            const maxDate = new Date();
            maxDate.setFullYear(maxDate.getFullYear());

            return (
              current.getTime() < minDate.getTime() ||
              current.getTime() > maxDate.getTime()
            );
          }}
          name='startDate'
          defaultValue={new Date()}
          format={{
            format: 'MM/YYYY',
            type: 'mask',
          }}
          suffixIcon={null}
          className='h-6 w-full grow rounded-3xl mb-3'
          placeholder={'MM / YYYY'}
          data-testid='birthDate'
        />

        {isFetchPatientGender && (
          <Select
            required
            showSearch
            filterOption={(input, option) =>
              ((option?.label ?? '') as string)
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            errors={errors.gender?.message}
            options={genderOptions}
            value={watch('gender')}
            name='gender'
            onChange={selectedValue => {
              onChangeField('gender', selectedValue);
            }}
            optionRender={optionRender}
            label={'Gender'}
            placeholder={'Choose Gender'}
            data-testid='gender'
          />
        )}
      </Modal>
      <UnsavedChangesModal
        onConfirm={() => toggleVisibility(false)}
        onCancel={() => setAskQuit(false)}
        open={askQuit}
      />
    </>
  );
};
